/** Colleague invites (#437) as the member portal words them. */
import type { ColleagueInvite } from "@/lib/api/colleague-invites";

export const COLLEAGUE_INVITES_KEY = ["colleague-invites"] as const;
/** Pushed on the company's portal sockets whenever an invite is created, decided or cancelled. */
export const INVITES_CHANGED_EVENT = "colleague_invites_changed";

export function inviteStatusBadge(invite: Pick<ColleagueInvite, "status">): { label: string; color: string } {
  switch (invite.status) {
    case "pending":
      return { label: "Waiting for approval", color: "orange" };
    case "approved":
      return { label: "Added", color: "green" };
    case "cancelled":
      return { label: "Cancelled", color: "gray" };
    default:
      return { label: "Declined", color: "red" };
  }
}

export interface InviteFormValues {
  email: string;
  first_name: string;
  last_name: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Client-side checks; the server repeats them (and more). */
export function inviteFormErrors(values: InviteFormValues): Partial<Record<keyof InviteFormValues, string>> {
  const errors: Partial<Record<keyof InviteFormValues, string>> = {};
  if (!EMAIL_RE.test(values.email.trim())) errors.email = "Enter a valid email address";
  if (!values.first_name.trim()) errors.first_name = "Required";
  if (!values.last_name.trim()) errors.last_name = "Required";
  return errors;
}

export const inviteeName = (invite: Pick<ColleagueInvite, "first_name" | "last_name" | "email">): string =>
  `${invite.first_name} ${invite.last_name}`.trim() || invite.email;

/** The toast after sending. One wording for every invite: the server's answer is deliberately the same whether it
 *  approved the invite at once or holds it for review (#437 review I2) — the decision arrives as a notification. */
export function inviteSubmittedMessage(invite: Pick<ColleagueInvite, "first_name" | "last_name" | "email">): string {
  return `Invite sent. You'll get a message when the invite for ${inviteeName(invite)} has been decided.`;
}

/** The server's refusal (django-ninja `{detail}`), else a generic retry message. */
export function inviteError(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : "Couldn't send the invite. Please try again.";
}
